// PAWSPhaseCard — Day 30+ "what's normal right now" surface (PR 1C, Gap 5).
//
// Users churn out of streak apps right when PAWS hits its stride. This
// card surfaces calibrated, phase-appropriate expectations once the user
// crosses Day 30:
//   - Day 30–45 → "the deepest dip" (anhedonia, RLS, sleep fragmenting)
//   - Day 45–90 → "daylight again" (sleep consolidating, mood ticking up)
//   - Day 90–180 → "the long tail" (mostly behaviour, occasional craving)
//   - Day 180+   → "the new normal" (anniversaries, three honest re-tests)
//
// Hides itself entirely for dayIndex < 30 (the existing Week 1–4 PAWS
// surfaces cover acute withdrawal already).
//
// Mobile discipline:
//  - Collapsible <details>, default-OPEN (the user is on Day 30+ — they've
//    earned the affordance of seeing what's normal up front).
//  - Header is ≥44px touch target; chevron rotates on open.
//  - Body uses a compact 3-bullet list + reassurance line. Fits 375px
//    without horizontal scroll, no wrapping awkwardness on category labels.
//  - Reduced-motion users get no chevron animation.

import { ChevronDown } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Icon } from '../../components/ui/Icon';
import { pawsLensForDay } from '../../content/pawsCalendar';

interface PAWSPhaseCardProps {
  dayIndex: number | null;
}

export function PAWSPhaseCard({ dayIndex }: PAWSPhaseCardProps) {
  if (dayIndex == null) return null;
  const lens = pawsLensForDay(dayIndex);
  if (!lens) return null;

  return (
    <Card className="paws-phase-card" accentLeft>
      <details className="paws-phase-details" open>
        <summary className="paws-phase-summary">
          <div className="paws-phase-summary-text">
            <span className="paws-phase-range">{lens.rangeLabel}</span>
            <span className="paws-phase-headline">{lens.headline}</span>
          </div>
          <Icon icon={ChevronDown} size={18} className="paws-phase-chevron" />
        </summary>
        <div className="paws-phase-body">
          <ul className="paws-phase-list">
            {lens.expected.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
          <p className="paws-phase-reassurance">{lens.reassurance}</p>
        </div>
      </details>
    </Card>
  );
}
